import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Checkbox from 'core/elements/input/Checkbox'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import DropdownToggle from 'core/elements/dropdown/DropdownToggle'

export interface MultiPicklistOption<T = any> {
  label: string
  value: T
}

interface Props<T = any> {
  id?: string
  label?: string
  className?: string
  value: T[]
  onChange: (values: T[]) => void
  options: MultiPicklistOption<T>[]
  required?: boolean
  error?: string
  disabled?: boolean
  loading?: boolean
  placeholder?: string
  getOptionLabel?: (option: T) => string
  isEqual?: (a: T, b: T) => boolean
}

export default function MultiPicklist<T = any>({
  id,
  label,
  className,
  value = [],
  onChange,
  options,
  required,
  error,
  disabled = false,
  loading = false,
  placeholder = 'Select options',
  getOptionLabel,
  isEqual,
}: Props<T>) {
  const classes = useStyles({ disabled, error })
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeDropdown])

  const isSelected = useCallback(
    (option: T): boolean => {
      if (isEqual) {
        return value.some((v) => isEqual(v, option))
      }
      return value.includes(option)
    },
    [value, isEqual],
  )

  const handleToggle = useCallback(
    (option: T) => {
      const selected = isSelected(option)
      const newValues = selected
        ? isEqual
          ? value.filter((v) => !isEqual(v, option))
          : value.filter((v) => v !== option)
        : [...value, option]
      onChange(newValues)
    },
    [value, onChange, isSelected, isEqual],
  )

  const handleSelectAll = useCallback(() => {
    const allValues = options.map((opt) => opt.value)
    onChange(allValues)
  }, [onChange, options])

  const handleDeselectAll = useCallback(() => {
    onChange([])
  }, [onChange])

  const getLabel = useCallback(
    (option: T): string => {
      if (getOptionLabel) {
        return getOptionLabel(option)
      }
      const found = options.find((opt) =>
        isEqual ? isEqual(opt.value, option) : opt.value === option,
      )
      return found?.label || String(option)
    },
    [options, getOptionLabel, isEqual],
  )

  const handleRemoveChip = useCallback(
    (option: T, e: React.MouseEvent) => {
      e.stopPropagation()
      const newValues = isEqual
        ? value.filter((v) => !isEqual(v, option))
        : value.filter((v) => v !== option)
      onChange(newValues)
    },
    [value, onChange, isEqual],
  )

  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange([])
    },
    [onChange],
  )

  const maxVisibleChips = 1
  const visibleChips = value.slice(0, maxVisibleChips)
  const remainingCount = value.length - maxVisibleChips

  const allSelected = value.length === options.length && options.length > 0

  return (
    <div className={clsx(classes.dropdownContainer, className)} ref={dropdownRef}>
      {label && (
        <Text variant="inputLabel" className={classes.label}>
          {label}
          {required && ' *'}
        </Text>
      )}
      <div
        className={classes.selectContainer}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        data-testid={id}
      >
        <div
          className={clsx(classes.inputFrame, 'inputFrame', {
            [classes.expandedInputFrame]: isOpen,
          })}
        >
          <div className={clsx(classes.chipsContainer, 'chipsContainer')}>
            {value.length === 0 ? (
              <Text variant="caption1" className={classes.placeholderText}>
                {loading ? 'Loading...' : placeholder}
              </Text>
            ) : (
              <>
                {visibleChips.map((chip, index) => (
                  <div key={index} className={classes.chip}>
                    <Text variant="caption1">{getLabel(chip)}</Text>
                    <span className={classes.chipRemove} onClick={(e) => handleRemoveChip(chip, e)}>
                      <FontAwesomeIcon solid size="sm">
                        close
                      </FontAwesomeIcon>
                    </span>
                  </div>
                ))}
                {remainingCount > 0 && (
                  <div className={classes.remainingBadge}>
                    <Text variant="caption1">+{remainingCount}</Text>
                  </div>
                )}
              </>
            )}
          </div>
          <div className={classes.actionsContainer}>
            {value.length > 0 && (
              <span className={classes.clearAll} onClick={handleClearAll}>
                <FontAwesomeIcon solid size="sm">
                  close
                </FontAwesomeIcon>
              </span>
            )}
            <DropdownToggle type="button">
              <FontAwesomeIcon solid size="sm">
                {isOpen ? 'caret-up' : 'caret-down'}
              </FontAwesomeIcon>
            </DropdownToggle>
          </div>
        </div>
        {isOpen && (
          <div
            className={clsx(classes.dropdownMenu, 'dropdownMenu')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={classes.selectAllContainer}>
              <Text
                variant="caption2"
                className={classes.selectAllLink}
                onClick={allSelected ? handleDeselectAll : handleSelectAll}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Text>
            </div>
            <div className={classes.checkboxContainer}>
              {loading ? (
                <Text variant="caption1" className={classes.loadingText}>
                  Loading...
                </Text>
              ) : (
                options.map((option, index) => (
                  <label
                    key={index}
                    className={classes.checkboxLabel}
                    data-testid={`${id}-${option.label}`}
                  >
                    <Checkbox
                      checked={isSelected(option.value)}
                      onChange={() => handleToggle(option.value)}
                    />
                    <Text variant="caption1">{option.label}</Text>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {!!error && (
        <Text variant="body2" className={clsx(classes.error, 'error')}>
          {error}
        </Text>
      )}
    </div>
  )
}

interface StyleProps {
  disabled?: boolean
  error?: string
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  dropdownContainer: {
    position: 'relative',
  },
  label: {
    marginLeft: 4,
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  selectContainer: {
    position: 'relative',
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
  },
  inputFrame: {
    ...theme.typography.inputPlaceholder,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    backgroundColor: ({ disabled }) =>
      disabled ? 'transparent' : theme.components.input.frame.background,
    color: theme.components.input.frame.color,
    borderRadius: 4,
    borderColor: ({ error }) =>
      error ? theme.components.input.error : theme.components.input.frame.border,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing(0, 1),
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
    minHeight: 36,
  },
  expandedInputFrame: {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  chipsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'nowrap',
    overflow: 'hidden',
    flex: 1,
  },
  placeholderText: {
    color: theme.palette.grey[500],
    fontWeight: 'normal',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    whiteSpace: 'nowrap',
  },
  chipRemove: {
    cursor: 'pointer',
    color: theme.palette.grey[500],
    fontSize: 14,
    lineHeight: 1,
    '&:hover': {
      color: theme.palette.grey[700],
    },
    '& i': {
      color: theme.palette.grey[500],
    },
  },
  remainingBadge: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    whiteSpace: 'nowrap',
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginRight: theme.spacing(2.5),
  },
  clearAll: {
    cursor: 'pointer',
    color: theme.palette.grey[500],
    fontSize: 14,
    lineHeight: 1,
    '&:hover': {
      color: theme.palette.grey[700],
    },
  },
  dropdownMenu: {
    width: '100%',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '0 0 4px 4px',
    borderTop: 'none',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    position: 'absolute',
    zIndex: 20,
    left: 0,
    top: '100%',
  },
  selectAllContainer: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  selectAllLink: {
    color: theme.palette.grey[500],
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    maxHeight: 200,
    overflowY: 'auto',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  loadingText: {
    padding: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  error: {
    marginLeft: 4,
    marginTop: theme.spacing(0.5),
    color: theme.components.dropdown.error,
    lineHeight: '16px',
  },
}))
